import type { LeadFieldTarget, LeadFieldType, LeadFormContent, LeadFormField } from '@/types/api'

/**
 * The rules the form designer applies before the server is asked.
 *
 * <p>
 * The server is the judge - it re-checks everything on save and on publish - but a designer
 * that offers "Saves to: phone number" on a checkbox, or mints a key the API refuses, is a
 * designer whose errors arrive one round trip late. These mirror `LeadFormSchema` in the API.
 * </p>
 */

export const FIELD_TYPES: readonly { type: LeadFieldType; label: string; glyph: string }[] = [
  { type: 'ShortText', label: 'Short text', glyph: 'Aa' },
  { type: 'Email', label: 'Email', glyph: '@' },
  { type: 'Phone', label: 'Phone', glyph: '#' },
  { type: 'LongText', label: 'Long text', glyph: '¶' },
  { type: 'Choice', label: 'Choice', glyph: '▾' },
  { type: 'Checkbox', label: 'Checkbox', glyph: '☐' },
  { type: 'Consent', label: 'Consent', glyph: '✓' },
]

export const TARGET_LABELS: Record<LeadFieldTarget, string> = {
  FullName: 'Name',
  CompanyName: 'Company',
  Email: 'Email address',
  Phone: 'Phone number',
  JobTitle: 'Job title',
  Message: 'Message',
}

/**
 * The lead columns a question of this kind may fill. An email question can only be the email
 * address; a short text can be a name, a company or a job title; a checkbox fills nothing and
 * is kept as a custom answer.
 */
export function targetsFor(type: LeadFieldType): LeadFieldTarget[] {
  switch (type) {
    case 'Email':
      return ['Email']
    case 'Phone':
      return ['Phone']
    case 'LongText':
      return ['Message']
    case 'ShortText':
      return ['FullName', 'CompanyName', 'JobTitle', 'Message']
    default:
      return []
  }
}

/** The API's pattern for a question key: a lower-case letter, then letters, digits and underscores. */
const KEY = /^[a-z][a-z0-9_]{0,39}$/

export function isValidKey(key: string): boolean {
  return KEY.test(key)
}

/**
 * A key from a label - "How many outlets?" becomes `how_many_outlets` - that does not clash
 * with any key already on the form.
 *
 * <p>
 * Keys are what an answer is stored under, so once a form is live they stay put even when
 * the label is reworded. Deriving one is only a convenience for a new question.
 * </p>
 */
export function keyFromLabel(label: string, taken: readonly string[]): string {
  let base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9_]+/, '')
    .slice(0, 34)
    .replace(/_+$/, '')

  if (!base) base = 'question'

  if (!taken.includes(base)) return base

  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`
    if (!taken.includes(candidate)) return candidate
  }
}

/** A new question of a kind, with the first column it can fill that nothing else fills yet. */
export function newField(type: LeadFieldType, fields: readonly LeadFormField[]): LeadFormField {
  const label = FIELD_TYPES.find((entry) => entry.type === type)?.label ?? 'Question'
  const used = new Set(fields.map((field) => field.target).filter(Boolean))
  const target = targetsFor(type).find((candidate) => !used.has(candidate)) ?? null

  return {
    key: keyFromLabel(
      type === 'Consent' ? 'consent' : label,
      fields.map((field) => field.key),
    ),
    type,
    label:
      type === 'Consent'
        ? 'I agree that Codemax may contact me about this enquiry and process my details under its privacy notice.'
        : label,
    required: type === 'Consent',
    target: type === 'ShortText' && target === 'Message' ? null : target,
    options: type === 'Choice' ? [{ value: 'option_1', label: 'Option 1' }] : null,
    display: type === 'Choice' ? 'dropdown' : null,
    width: 'full',
    helpText: null,
    placeholder: null,
  }
}

/** Moves the question at `from` one place up or down, leaving the array untouched if it cannot. */
export function moveField<T>(fields: readonly T[], from: number, by: -1 | 1): T[] {
  const to = from + by
  if (to < 0 || to >= fields.length) return [...fields]

  const next = [...fields]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved as T)
  return next
}

/**
 * What the designer can already see is wrong before publishing, worded as the API words it.
 * The server's list is the one that decides; this is shown while the draft is unsaved.
 */
export function localProblems(content: LeadFormContent): string[] {
  const problems: string[] = []
  const fields = content.fields

  if (!content.title.trim()) problems.push('The form needs a title.')
  if (!fields.some((field) => field.target === 'FullName')) problems.push("The form must ask for the person's name.")
  if (!fields.some((field) => field.target === 'Email' || field.target === 'Phone')) {
    problems.push('The form must ask for an email address or a phone number.')
  }
  if (fields.filter((field) => field.type === 'Consent').length !== 1) {
    problems.push('The form must have exactly one consent question.')
  }
  if (fields.some((field) => !field.label.trim())) problems.push('Every question needs a label.')

  return problems
}
